// SingularityEntry.tsx - One-Click Spatial OS Entry
// Pure void black screen with cyan Initialize Uplink button

import React, { useState, useCallback } from 'react'

interface SingularityEntryProps {
  onEnter: () => void
}

export const SingularityEntry: React.FC<SingularityEntryProps> = ({ onEnter }) => {
  const [isFading, setIsFading] = useState(false)
  const [fadeOpacity, setFadeOpacity] = useState(1)

  const handleInitialize = useCallback(async () => {
    // Request pointer lock for immersive control
    try {
      await document.body.requestPointerLock()
    } catch (err) {
      console.warn('[Singularity] Pointer lock request failed:', err)
    }

    // Start fade animation
    setIsFading(true)
    
    // Animate opacity from 1 to 0 over 1.5s
    const startTime = Date.now()
    const duration = 1500
    
    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const newOpacity = 1 - progress
      
      setFadeOpacity(newOpacity)
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        // Fade complete - trigger entry
        onEnter()
      }
    }
    
    requestAnimationFrame(animate)
  }, [onEnter])

  return (
    <div
      className="singularity-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        opacity: fadeOpacity,
        pointerEvents: isFading ? 'none' : 'auto',
        transition: isFading ? 'none' : 'opacity 0.3s ease'
      }}
    >
      <button
        onClick={handleInitialize}
        className="singularity-button"
        style={{
          background: 'transparent',
          border: '1px solid #00ffff',
          borderRadius: 0,
          padding: '16px 32px',
          color: '#00ffff',
          fontFamily: "'JetBrains Mono', 'Fira Code', 'SF Mono', monospace",
          fontSize: '14px',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden',
          textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
          boxShadow: '0 0 20px rgba(0, 255, 255, 0.1), inset 0 0 20px rgba(0, 255, 255, 0.05)',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 0 40px rgba(0, 255, 255, 0.3), inset 0 0 30px rgba(0, 255, 255, 0.1)'
          e.currentTarget.style.textShadow = '0 0 20px rgba(0, 255, 255, 0.8)'
          e.currentTarget.style.borderColor = '#00ffff'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 255, 255, 0.1), inset 0 0 20px rgba(0, 255, 255, 0.05)'
          e.currentTarget.style.textShadow = '0 0 10px rgba(0, 255, 255, 0.5)'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ 
            width: '8px', 
            height: '8px', 
            background: '#00ffff',
            boxShadow: '0 0 10px #00ffff',
            animation: 'pulse 2s ease-in-out infinite'
          }} />
          [ ENTER THE AETHER ]
        </span>
      </button>

      {/* Subtle scanline effect */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 255, 255, 0.01) 2px, rgba(0, 255, 255, 0.01) 4px)',
          pointerEvents: 'none'
        }}
      />

      {/* Corner brackets */}
      <div className="singularity-corner top-left" />
      <div className="singularity-corner top-right" />
      <div className="singularity-corner bottom-left" />
      <div className="singularity-corner bottom-right" />
    </div>
  )
}

export default SingularityEntry
